/**
 * Static Tags Picker - single or multi-select chips for assigning static tags.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Tag } from 'lucide-react';

import { useStaticTags } from '@/hooks';
import { LoadingSpinner } from '@/components/ui';

export interface StaticTagsPickerSectionMultiProps {
  mode?: 'multi';
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  compact?: boolean;
}

export interface StaticTagsPickerSectionSingleProps {
  mode: 'single';
  selectedId: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  compact?: boolean;
  /** When true, shows "— Leave unchanged —" option (for bulk edit). */
  allowUnchanged?: boolean;
}

export type StaticTagsPickerSectionProps =
  | StaticTagsPickerSectionMultiProps
  | StaticTagsPickerSectionSingleProps;

export const StaticTagsPickerSection: FC<StaticTagsPickerSectionProps> = (props) => {
  const { disabled = false, compact = false } = props;
  const { data: tags, isLoading } = useStaticTags();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-1">
        <LoadingSpinner size="sm" />
        <span className="text-xs text-muted-foreground">Loading tags…</span>
      </div>
    );
  }

  if (!tags?.length) return null;

  if (props.mode === 'single') {
    const { selectedId, onChange, allowUnchanged = false } = props;
    const toggle = (id: string) => {
      if (disabled) return;
      onChange(selectedId === id ? null : id);
    };
    return (
      <div data-testid="static-tags-picker-single" className={compact ? 'space-y-1.5' : 'space-y-2'}>
        <div className="flex items-center gap-1.5">
          <Tag className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">Static tags</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {allowUnchanged && (
            <button
              type="button"
              onClick={() => (disabled ? undefined : onChange(selectedId === 'unchanged' ? null : 'unchanged'))}
              disabled={disabled}
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 ${
                selectedId === 'unchanged'
                  ? 'border-primary bg-primary/15 text-primary'
                  : 'border-border bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              — Leave unchanged —
            </button>
          )}
          {tags.map((t) => {
            const selected = selectedId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                disabled={disabled}
                className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 ${
                  selected
                    ? 'border-transparent text-white'
                    : 'border-border bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                style={selected ? { backgroundColor: t.color, color: '#fff' } : undefined}
              >
                {t.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const { selectedIds, onChange } = props;
  const toggle = (id: string) => {
    if (disabled) return;
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };
  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      <div className="flex items-center gap-1.5">
        <Tag className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Static tags</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => {
          const selected = selectedIds.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => toggle(t.id)}
              disabled={disabled}
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 ${
                selected
                  ? 'border-transparent text-white'
                  : 'border-border bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              style={selected ? { backgroundColor: t.color, color: '#fff' } : undefined}
            >
              {t.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};
